import React from 'react';
import { ampelColor, deltaColor } from './KbsLayer';
import { MIN_TRAINS_FOR_VERDICT, HIGH_DELAY_THRESHOLD_MIN } from './trainPosition';
import { routeLabel } from './routeLabel';

const fmtPct = (v) => (v === null || v === undefined ? '–' : `${v.toFixed(1)} %`);
const fmtMin = (v) => (v === null || v === undefined ? '–' : `${v >= 0 ? '+' : ''}${v.toFixed(1)} min`);

/**
 * Drill-in for one Kursbuchstrecke — wireframe 2 of the dashboard concept:
 * headline figures, delay over the day, top delayed trains, where delay was picked up.
 * Charts are plain SVG/CSS on purpose; no chart library is pulled in for this.
 */
const KbsDrillIn = ({
  detail, route, opDay, loading, failed = false, pinned, onTogglePin, onShowPreviousDay, onClose,
}) => {
  // The panel always opens. It used to render nothing when the day had no figures for the
  // route, so a click on it looked like a click that did nothing. The heading comes from the
  // detail, or from the route geometry when there is none.
  const head = detail || route || {};

  // Every block below reads a list. They arrive together with the detail form and are all
  // absent from the summary form, so one guard each rather than one for the object.
  const hourly = detail?.hourly || [];
  const topDelayed = detail?.topDelayed || [];
  const segments = detail?.segments || [];
  // An hour below the minimum sample carries NULL, not zero — the aggregate says "no
  // statement" and the bar has to leave it out rather than draw a punctual hour. Reading it
  // as a number would also have thrown on .toFixed().
  const hasDelay = (h) => typeof h.delay_avg_min === 'number';
  const maxDelay = Math.max(1, ...hourly.filter(hasDelay).map(h => h.delay_avg_min));

  return (
    <aside className="kbs-drillin">
      <div className="kbs-drillin-header">
        <button className="kbs-drillin-close" onClick={onClose} title="Schließen">
          <i className="fa fa-arrow-left"></i>
        </button>
        <div className="kbs-drillin-headtext">
          <div className="kbs-drillin-title">
            {routeLabel(head.kbs_number, head.country)}
            {head.kbs_name && <span className="kbs-drillin-name">{head.kbs_name}</span>}
          </div>
          <div className="kbs-drillin-date">Betriebstag {opDay} · 04:00–04:00</div>
        </div>
        <button
          className={`kbs-drillin-pin ${pinned ? 'active' : ''}`}
          onClick={onTogglePin}
          title={pinned ? 'Markierung aufheben' : 'Strecke markieren — bleibt auf der Karte hervorgehoben'}
        >
          <i className="fa fa-thumb-tack"></i>
        </button>
      </div>

      {loading && (
        <div className="kbs-drillin-loading"><i className="fa fa-spinner fa-spin"></i> lädt…</div>
      )}

      {!loading && !detail && (
        <div className="kbs-drillin-empty">
          {failed ? (
            <p>
              <i className="fa fa-exclamation-triangle"></i> Die Auswertung für diese Strecke
              konnte nicht geladen werden.
            </p>
          ) : (
            <>
              <p>
                <i className="fa fa-info-circle"></i> Für den Betriebstag {opDay} liegt für diese
                Strecke keine Auswertung vor — entweder fuhren hier keine erfassten Züge, oder der
                Tag ist noch nicht ausgewertet.
              </p>
              {onShowPreviousDay && (
                <button className="kbs-drillin-prevday" onClick={onShowPreviousDay}>
                  <i className="fa fa-chevron-left"></i> Vortag anzeigen
                </button>
              )}
            </>
          )}
        </div>
      )}

      {detail && (
        <>
          <div className="kbs-drillin-kpis">
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{detail.train_count}</span>
              <span className="kbs-kpi-label">Züge</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value" style={{ color: ampelColor(detail.on_time_pct) }}>
                {fmtPct(detail.on_time_pct)}
              </span>
              <span className="kbs-kpi-label">pünktlich</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{fmtMin(detail.delay_avg_min)}</span>
              <span className="kbs-kpi-label">Ø Verspätung</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value" style={{ color: deltaColor(detail.delta_avg_min) }}>
                {fmtMin(detail.delta_avg_min)}
              </span>
              <span className="kbs-kpi-label">Ø Zuwachs</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{detail.delay_median_min === null || detail.delay_median_min === undefined
                  ? '–' : `${detail.delay_median_min} min`}</span>
              <span className="kbs-kpi-label">Median</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{detail.high_delay_count}</span>
              <span className="kbs-kpi-label" title={`ab ${HIGH_DELAY_THRESHOLD_MIN} min`}>Hochversp.</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{detail.cancelled_count}</span>
              <span className="kbs-kpi-label">Ausfälle</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{detail.track_change_count ?? 0}</span>
              <span className="kbs-kpi-label" title="Abweichung vom planmäßigen Gleis">Gleisänd.</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{detail.movement_count}</span>
              <span className="kbs-kpi-label">Halte</span>
            </div>
          </div>

          {!detail.enough_data && (
            <p className="kbs-thin-data">
              <i className="fa fa-info-circle"></i> Weniger als {MIN_TRAINS_FOR_VERDICT} Züge —
              Pünktlichkeit und Zuwachs werden nicht bewertet.
            </p>
          )}

          <div className="kbs-drillin-section">
            <h5>Verspätung über den Tagesverlauf</h5>
            <div className="kbs-hourly">
              {hourly.map(h => (
                <div key={h.hour} className="kbs-hour-col" title={
                  h.movements > 0
                    ? `${String(h.hour).padStart(2, '0')}:00 — ${h.movements} Halte, Ø ${hasDelay(h) ? `${h.delay_avg_min.toFixed(1)} min` : 'keine Aussage'}, ${fmtPct(h.on_time_pct)} pünktlich`
                    : `${String(h.hour).padStart(2, '0')}:00 — keine Fahrten`
                }>
                  <div
                    className="kbs-hour-bar"
                    style={{
                      height: hasDelay(h) ? `${(h.delay_avg_min / maxDelay) * 100}%` : '0%',
                      background: ampelColor(h.on_time_pct),
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="kbs-hourly-axis">
              {[0, 4, 8, 12, 16, 20].map(h => (
                <span key={h}>{String(h).padStart(2, '0')}</span>
              ))}
            </div>
          </div>

          <div className="kbs-drillin-section">
            <h5>Top verspätete Züge</h5>
            {topDelayed.length === 0 && <p className="kbs-empty">Keine Daten</p>}
            <ol className="kbs-top-list">
              {topDelayed.map((t, i) => (
                <li key={`${t.trainName}-${i}`}>
                  <span className="kbs-top-name">{t.trainName}</span>
                  <span className="kbs-top-meta">{t.trainLineName} → {t.terminus}</span>
                  <span className="kbs-top-delay">+{t.delay} min</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="kbs-drillin-section">
            <h5>Ø Zuwachs pro Abschnitt — wo entstand Verspätung?</h5>
            {segments.length === 0 && (
              <p className="kbs-empty">
                Kein Abschnitt erreicht {MIN_TRAINS_FOR_VERDICT} Züge.
              </p>
            )}
            <ul className="kbs-segment-list">
              {segments.slice(0, 8).map(seg => (
                <li key={seg.section}>
                  <span className="kbs-segment-name">{seg.section}</span>
                  <span className="kbs-segment-count">{seg.count}×</span>
                  <span className="kbs-segment-delta" style={{ color: deltaColor(seg.delta_avg_min) }}>
                    {fmtMin(seg.delta_avg_min)}
                    {seg.delta_avg_min > 1 && <i className="fa fa-exclamation-triangle"></i>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </aside>
  );
};

export default KbsDrillIn;
